/**
 * Patron categories and their loan rules: each category's identity and default
 * rules, their exceptions by item type and location, and a simulator of the
 * applied rule.
 */

import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, Plus } from "lucide-react";
import { useLocations, usePatronCategories } from "@/api/hooks";
import type { PatronCategory } from "@/api/types";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { CategoryDefaultsCard } from "../components/loan-rules/CategoryDefaultsCard";
import { PolicyExceptions } from "../components/loan-rules/PolicyExceptions";
import { RuleSimulator } from "../components/loan-rules/RuleSimulator";
import { CategoryDialog } from "../components/patron-categories/CategoryDialog";
import { CategoryIdentity } from "../components/patron-categories/CategoryIdentity";
import { plural } from "../loanRules";

export function LoanRulesPage() {
  const categories = usePatronCategories();
  const locationsQuery = useLocations();
  const [searchParams, setSearchParams] = useSearchParams();
  // "new" opens the creation drawer, a category its identity editor
  const [dialog, setDialog] = useState<PatronCategory | "new" | null>(null);

  const locations = useMemo(
    () =>
      (locationsQuery.data ?? [])
        .filter((location) => location.active)
        .sort((a, b) => a.name.localeCompare(b.name, "fr")),
    [locationsQuery.data]
  );

  // The selected category lives in the URL so a rule set can be linked to directly
  const selected =
    categories.data?.find((category) => category.id === searchParams.get("categorie")) ??
    categories.data?.[0];

  const selectCategory = (id: string | null) => {
    setSearchParams(id ? { categorie: id } : {}, { replace: true });
  };

  const newCategoryButton = (
    <Button
      type="button"
      onClick={() => {
        setDialog("new");
      }}
    >
      <Plus aria-hidden />
      Nouvelle catégorie
    </Button>
  );

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Catégories et règles de prêt
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500 dark:text-gray-400">
            Chaque catégorie de lecteurs a ses règles par défaut. Une exception les remplace pour un
            type de document ou un site : la règle la plus précise l&apos;emporte.
          </p>
        </div>
        {selected && newCategoryButton}
      </header>

      {categories.isLoading || locationsQuery.isLoading ? (
        <div
          aria-busy="true"
          aria-label="Chargement des règles"
          className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]"
        >
          <div className="h-64 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
          <div className="h-96 animate-pulse rounded-2xl bg-gray-100 dark:bg-gray-800" />
        </div>
      ) : categories.error || locationsQuery.error ? (
        <p
          role="alert"
          className="flex items-center gap-2 rounded-lg bg-red-50 p-4 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          <AlertCircle className="h-4 w-4" aria-hidden />
          Impossible de charger les catégories de lecteurs ou les sites.
        </p>
      ) : !selected ? (
        <div className="flex flex-col items-center rounded-2xl border border-dashed border-gray-300 px-6 py-14 text-center dark:border-gray-600">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            Aucune catégorie de lecteurs
          </h2>
          <p className="mt-1 mb-5 max-w-md text-sm text-gray-500 dark:text-gray-400">
            Chaque lecteur inscrit appartient à une catégorie, qui fixe ses droits de prêt. Créez la
            première pour pouvoir inscrire des lecteurs.
          </p>
          {newCategoryButton}
        </div>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <nav aria-label="Catégories de lecteurs" className="lg:sticky lg:top-6">
            <ul className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible lg:pb-0">
              {(categories.data ?? []).map((category) => (
                <li key={category.id} className="shrink-0">
                  <CategoryTab
                    category={category}
                    selected={category.id === selected.id}
                    onSelect={() => {
                      selectCategory(category.id);
                    }}
                  />
                </li>
              ))}
            </ul>
          </nav>

          {/* Keyed by category so drafts never carry over to another category */}
          <div key={selected.id} className="min-w-0 space-y-6">
            <CategoryIdentity
              category={selected}
              onEdit={() => {
                setDialog(selected);
              }}
            />
            <CategoryDefaultsCard category={selected} />
            <PolicyExceptions category={selected} locations={locations} />
            <RuleSimulator category={selected} locations={locations} />
          </div>
        </div>
      )}

      {dialog !== null && (
        <CategoryDialog
          categories={categories.data ?? []}
          category={dialog === "new" ? null : dialog}
          onClose={() => {
            setDialog(null);
          }}
          onSaved={(saved) => {
            setDialog(null);
            selectCategory(saved?.id ?? null);
          }}
        />
      )}
    </div>
  );
}

function CategoryTab({
  category,
  selected,
  onSelect,
}: {
  category: PatronCategory;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "page" : undefined}
      className={cn(
        "w-full rounded-xl border px-4 py-3 text-left transition-colors",
        selected
          ? "border-primary/40 bg-primary/5 dark:bg-primary/10"
          : "border-transparent hover:bg-gray-100 dark:hover:bg-gray-800"
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span
          className={cn(
            "text-sm font-medium",
            selected ? "text-primary" : "text-gray-900 dark:text-white"
          )}
        >
          {category.name}
        </span>
        {!category.active && (
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500 dark:bg-gray-700 dark:text-gray-300">
            inactive
          </span>
        )}
      </span>
      <span className="mt-0.5 block text-xs whitespace-nowrap text-gray-500 tabular-nums dark:text-gray-400">
        {plural(category.defaultLoanDays, "jour")} · {plural(category.maxLoans, "document")} ·{" "}
        {plural(category.maxRenewals, "prolongation")}
      </span>
    </button>
  );
}
